import type { KeyboardEvent, ReactNode } from 'react'
import {
  Brain,
  FileText,
  MessageCircle,
  Mic,
  Plus,
  Send,
  Slash,
  Square,
  StopCircle,
  TextSelect,
  Zap,
} from 'lucide-react'
import { cn } from '../lib/cn'
import { Segmented } from '../primitives/Segmented'

export type Scope = 'document' | 'selection' | 'none'
export type ChatMode = 'fast' | 'thinking'

export interface ComposerProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  scope: Scope
  onScopeChange: (scope: Scope) => void
  mode: ChatMode
  onModeChange: (mode: ChatMode) => void
  busy?: boolean
  onStop?: () => void
  onMic?: () => void
  dictating?: boolean
  onPlus?: () => void
  onSlash?: () => void
  placeholder?: string
  /** Host-aware label for the document scope (e.g. "Ganze E-Mail" in Outlook). */
  scopeDocumentLabel?: string
  /** Context/attachment chips rendered above the input (pools, local files). */
  chips?: ReactNode
  /**
   * Intercept key events in the input before the default handling (Enter → send).
   * Return `true` if the key was handled (e.g. slash-menu navigation) to suppress it.
   */
  onInputKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean
}

export function Composer({
  value,
  onChange,
  onSend,
  scope,
  onScopeChange,
  mode,
  onModeChange,
  busy,
  onStop,
  onMic,
  dictating,
  onPlus,
  onSlash,
  placeholder = 'Nachricht an Text Assistent…',
  scopeDocumentLabel = 'Ganzes Dokument',
  chips,
  onInputKeyDown,
}: ComposerProps) {
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (onInputKeyDown?.(e)) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSend()
    }
  }

  return (
    <div className="border-t border-neutral-200 p-3">
      <div className="rounded-xl border border-neutral-200 p-2.5 focus-within:border-brand-300">
        <div className="mb-2">
          <Segmented
            value={scope}
            onChange={onScopeChange}
            size="sm"
            activeTone={scope === 'selection' ? 'scope' : 'brand'}
            options={[
              {
                value: 'document',
                label: scopeDocumentLabel,
                icon: <FileText className="h-3.5 w-3.5" />,
              },
              {
                value: 'selection',
                label: 'Auswahl',
                icon: <TextSelect className="h-3.5 w-3.5" />,
              },
              {
                value: 'none',
                label: 'Ohne Kontext',
                icon: <MessageCircle className="h-3.5 w-3.5" />,
              },
            ]}
          />
        </div>

        {chips && <div className="mb-2 flex flex-wrap gap-1.5">{chips}</div>}

        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={placeholder}
          className="max-h-32 w-full resize-none bg-transparent px-1 py-1 text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
        />

        <div className="mt-1 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <SquareButton label="Anhängen" onClick={onPlus}>
              <Plus className="h-4 w-4" />
            </SquareButton>
            <SquareButton label="Skills & Befehle" onClick={onSlash}>
              <Slash className="h-4 w-4" />
            </SquareButton>
          </div>

          <div className="flex items-center gap-1.5">
            <ModeToggle mode={mode} onModeChange={onModeChange} />
            {dictating ? (
              <button
                type="button"
                onClick={onMic}
                aria-label="Diktat stoppen"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
              >
                <StopCircle className="h-4 w-4" />
              </button>
            ) : (
              <SquareButton label="Diktieren" onClick={onMic}>
                <Mic className="h-4 w-4" />
              </SquareButton>
            )}
            {busy ? (
              <button
                type="button"
                onClick={onStop}
                aria-label="Stoppen"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-500 text-white hover:bg-red-600"
              >
                <Square className="h-3.5 w-3.5" fill="currentColor" />
              </button>
            ) : (
              <button
                type="button"
                onClick={onSend}
                aria-label="Senden"
                disabled={!value.trim()}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white hover:bg-brand-600 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ModeToggle({
  mode,
  onModeChange,
}: {
  mode: ChatMode
  onModeChange: (mode: ChatMode) => void
}) {
  return (
    <div className="inline-flex items-center rounded-full bg-neutral-100 p-0.5">
      <button
        type="button"
        onClick={() => onModeChange('fast')}
        aria-label="Schneller Modus"
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
          mode === 'fast' ? 'bg-white text-brand-600 shadow-sm' : 'text-neutral-400',
        )}
      >
        <Zap className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onModeChange('thinking')}
        aria-label="Nachdenken-Modus"
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
          mode === 'thinking' ? 'bg-white text-reasoning shadow-sm' : 'text-neutral-400',
        )}
      >
        <Brain className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function SquareButton({
  children,
  label,
  onClick,
}: {
  children: ReactNode
  label: string
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 text-neutral-500 hover:bg-neutral-50"
    >
      {children}
    </button>
  )
}
